import { createContext, useContext } from 'react';
import {
    KanbanCard,
    KanbanColumn,
    KanbanColumnGroup,
    KanbanRenderProps,
    KanbanInfiniteScrollProps
} from './HBKanban.types';

export interface HBKanbanContextValue<TItemData = any, TColumnData = any> {
    items: Record<string, KanbanCard<TItemData>[]>;
    columns: KanbanColumn<TColumnData>[];
    groups?: KanbanColumnGroup[];
    renderProps?: KanbanRenderProps<TItemData, TColumnData>;

    // Card interaction
    onCardClick?: (
        card: KanbanCard<TItemData>,
        event: React.MouseEvent
    ) => void;

    // Selection state
    isCardSelected: (card: KanbanCard<TItemData>) => boolean;
    onToggleSelect?: (card: KanbanCard<TItemData>) => void;
    isInSelectableState: boolean;

    // Drag state
    activeIds?: string[];
    overId: string | null;
    draggingOverNonDroppable: string | null;

    // Drag drop handlers (internal - accept dnd-kit events)
    handleDragStart: (event: any) => void;
    handleDragOver: (event: any) => void;
    handleDragEnd: (event: any) => void;

    // Helper functions
    getCardById: (cardId: string) => KanbanCard<TItemData> | undefined;
    getActiveCard: () => KanbanCard<TItemData> | null;
    canDropInColumn: (cardId: string, columnId: string) => boolean;
    getSourceColumnForCard: (cardId: string) => string | null;

    isLoadingColumn: (columnId: string) => boolean;
    onLoadMore: (columnId: string, page: number) => void;

    infiniteScrollProps: KanbanInfiniteScrollProps<TItemData>;

    // Column counts (includes paginated items, updated by drag operations)
    columnCounts: Record<string, number>;

    // Card deletion - returns a revertDelete function that can be called if deletion fails
    removeCard: (cardId: string) => (() => void) | undefined;

    // Configuration
    testIdName?: string;

    // Container ref for boundary checking
    containerRef?: React.RefObject<HTMLDivElement>;

    // Column content refs for scrolling
    columnContentRefs: Map<string, React.RefObject<HTMLDivElement>>;
    registerColumnContentRef: (
        columnId: string,
        ref: React.RefObject<HTMLDivElement>
    ) => void;

    // Column element refs for positioning (e.g., group badges)
    columnRefs: Map<string, React.RefObject<HTMLDivElement>>;
    registerColumnRef: (
        columnId: string,
        ref: React.RefObject<HTMLDivElement>
    ) => void;
    // Version counter that increments when column refs are registered
    columnRefsVersion: number;

    // Virtualization props
    virtualization?:
        | { mode: 'on' }
        | { mode: 'off' }
        | { mode: 'auto'; threshold?: number };

    horizontalPadding?: number;
}

export const HBKanbanContext = createContext<HBKanbanContextValue | null>(null);

/**
 * Custom hook to access HBKanban context
 * @throws Error if used outside of HBKanbanProvider
 */
export const useHBKanbanContext = <
    TItemData = any,
    TColumnData = any
>(): HBKanbanContextValue<TItemData, TColumnData> => {
    const context = useContext(HBKanbanContext);

    if (!context) {
        throw new Error(
            'useHBKanbanContext must be used within HBKanbanProvider'
        );
    }

    return context as HBKanbanContextValue<TItemData, TColumnData>;
};
